import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Plus, Boxes, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import DashboardLayout from '../../components/DashboardLayout';
import { inventoryAPI } from '../../services/api';
import AssetTabs from './AssetTabs';
import AddInventoryTransaction from './AddInventoryTransaction';

const TXN_LABELS = { RESTOCK: 'Restock', ISSUE: 'Issue', RETURN: 'Return' };

export default function InventoryDetails() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTxn, setShowTxn] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [itemRes, txnRes] = await Promise.all([
        inventoryAPI.get(id),
        inventoryAPI.getTransactions(id),
      ]);
      setItem(itemRes.data);
      setTransactions(txnRes.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [id]);

  const handleDelete = async () => {
    if (!window.confirm('Delete this inventory item?')) return;
    try {
      await inventoryAPI.delete(id);
      window.location.href = '/dashboard/assets/inventory';
    } catch (e) {
      alert(e.message);
    }
  };

  if (loading || !item) {
    return (
      <DashboardLayout>
        <div className="py-12 text-center text-ga-text-muted">Loading...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/dashboard/assets/inventory" aria-label="Back to inventory" className="p-2 rounded-lg text-ga-text-primary hover:bg-ga-bg-surface-subtle">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-ga-text-primary flex items-center gap-2">
                <Boxes className="h-6 w-6 text-ga-primary" />
                {item.item_name}
              </h1>
              <p className="text-ga-text-muted">{item.category || 'Uncategorized'}</p>
              {(item.make || item.colour || item.size || item.model) && (
                <p className="text-sm text-ga-text-subtle mt-0.5">
                  {[item.make, item.model, item.colour, item.size].filter(Boolean).join(' · ')}
                </p>
              )}
            </div>
          </div>
          <button onClick={handleDelete} aria-label="Delete item" className="p-2 text-ga-danger hover:bg-ga-danger/10 rounded-lg">
            <Trash2 className="h-5 w-5" />
          </button>
        </div>

        <AssetTabs active="inventory" />

        <div className="ga-card grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-ga-text-subtle">On hand</p>
            <p className="text-2xl font-bold text-ga-text-primary">
              {Number(item.quantity_on_hand).toLocaleString()} <span className="text-sm font-normal">{item.unit}</span>
            </p>
          </div>
          <div>
            <p className="text-xs text-ga-text-subtle">Reorder at</p>
            <p className="text-lg text-ga-text-primary">
              {item.reorder_threshold != null ? `${Number(item.reorder_threshold).toLocaleString()} ${item.unit}` : '-'}
            </p>
          </div>
          <div>
            <p className="text-xs text-ga-text-subtle">Location</p>
            <p className="text-lg text-ga-text-primary">{item.location || '-'}</p>
          </div>
          <div>
            <p className="text-xs text-ga-text-subtle">Status</p>
            <p className="text-lg text-ga-text-primary">{item.status}</p>
            {item.is_low_stock && (
              <span className="text-xs text-ga-danger font-medium">Low stock</span>
            )}
          </div>
        </div>

        <div className="ga-card grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-ga-text-subtle">Make</p>
            <p className="text-lg text-ga-text-primary">{item.make || '-'}</p>
          </div>
          <div>
            <p className="text-xs text-ga-text-subtle">Model</p>
            <p className="text-lg text-ga-text-primary">{item.model || '-'}</p>
          </div>
          <div>
            <p className="text-xs text-ga-text-subtle">Colour</p>
            <p className="text-lg text-ga-text-primary">{item.colour || '-'}</p>
          </div>
          <div>
            <p className="text-xs text-ga-text-subtle">Size</p>
            <p className="text-lg text-ga-text-primary">{item.size || '-'}</p>
          </div>
        </div>

        {item.notes && (
          <div className="ga-card">
            <p className="text-sm text-ga-text-muted">{item.notes}</p>
          </div>
        )}

        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-ga-text-primary">Transaction history</h2>
            <button onClick={() => setShowTxn(true)} className="ga-btn-primary inline-flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Restock / Issue / Return
            </button>
          </div>

          {transactions.length === 0 ? (
            <div className="ga-card py-8 text-center text-ga-text-muted">No transactions yet</div>
          ) : (
            <div className="ga-card overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-ga-border-subtle">
                    <th className="text-left py-2 px-2 text-sm text-ga-text-muted">Date</th>
                    <th className="text-left py-2 px-2 text-sm text-ga-text-muted">Type</th>
                    <th className="text-left py-2 px-2 text-sm text-ga-text-muted">Qty</th>
                    <th className="text-left py-2 px-2 text-sm text-ga-text-muted">By</th>
                    <th className="text-left py-2 px-2 text-sm text-ga-text-muted">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((t) => (
                    <tr key={t.id} className="border-b border-ga-border-subtle/50">
                      <td className="py-2 px-2 text-sm">{format(new Date(t.created_at), 'dd MMM yyyy HH:mm')}</td>
                      <td className="py-2 px-2 text-sm">{TXN_LABELS[t.transaction_type] || t.transaction_type}</td>
                      <td className="py-2 px-2 text-sm font-medium">{Number(t.quantity).toLocaleString()} {item.unit}</td>
                      <td className="py-2 px-2 text-sm text-ga-text-muted">{t.performed_by_name || '-'}</td>
                      <td className="py-2 px-2 text-sm text-ga-text-muted">{t.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {showTxn && (
          <AddInventoryTransaction
            item={item}
            onClose={() => setShowTxn(false)}
            onSaved={() => { setShowTxn(false); fetchData(); }}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
